import {
  Menu, Users, Image, Home, ChevronDown, GraduationCap, Calendar, Lightbulb,
  Building2, Briefcase, PiggyBank, BookOpen, Link as LinkIcon, Scale,
} from "lucide-react";
import { useLocation } from "wouter";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { WeatherWidget } from "@/components/weather-widget";
import { useState } from "react";
import { useLogo } from "@/hooks/use-logo";

/** A page link inside a nav group. */
interface NavLink {
  href: string;
  label: string;
  icon: any;
}

/**
 * Grouped navigation. The site outgrew a flat bar once Jobs & Exams, Schemes
 * and Student Resources were added, so related pages now sit under four
 * top-level menus.
 */
const NAV_GROUPS: { id: string; label: string; icon: any; links: NavLink[] }[] = [
  {
    id: "village",
    label: "Village",
    icon: Users,
    links: [
      { href: "/community", label: "Community", icon: Users },
      { href: "/know-your-officials", label: "Know Your Officials", icon: Building2 },
      { href: "/gallery", label: "Gallery", icon: Image },
      { href: "/events", label: "Events", icon: Calendar },
    ],
  },
  {
    id: "careers",
    label: "Careers",
    icon: Briefcase,
    links: [
      { href: "/jobs-exams", label: "Jobs & Exams", icon: Briefcase },
      { href: "/student-resources", label: "Student Resources", icon: GraduationCap },
    ],
  },
  {
    id: "services",
    label: "Services",
    icon: PiggyBank,
    links: [
      { href: "/schemes-sewa", label: "Schemes & Sewa", icon: PiggyBank },
      { href: "/student-section", label: "Info & Links", icon: LinkIcon },
      { href: "/must-know-laws", label: "Must Know Laws", icon: Scale },
      { href: "/awareness", label: "Awareness", icon: Lightbulb },
    ],
  },
];

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [homeMenuOpen, setHomeMenuOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [location, setLocation] = useLocation();
  const { logoUrl } = useLogo();

  const scrollToSection = (id: string) => {
    if (location !== "/") {
      setLocation("/");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setMobileMenuOpen(false);
  };

  const navItems = [
    { id: "leadership", label: "Leadership" },
    { id: "statistics", label: "Statistics" },
    { id: "community", label: "Community" },
    { id: "services", label: "Services" },
    { id: "links", label: "Government Links" },
  ];

  const go = (href: string) => {
    setLocation(href);
    setMobileMenuOpen(false);
  };

  /** True when the current page belongs to this group, so it can be highlighted. */
  const groupActive = (links: NavLink[]) => links.some((l) => l.href === location);

  return (
    <header
      className="sticky top-0 z-50 bg-background neo-card-flat"
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 md:h-24">
          <button
            onClick={() => setLocation("/")}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer -ml-1"
            data-testid="button-header-logo"
            aria-label="Go to home page"
          >
            {logoUrl && (
              <div className="w-20 h-20 md:w-24 md:h-24 flex-shrink-0 flex items-center justify-center">
                <img
                  src={logoUrl}
                  alt="Sandalpur Logo"
                  className="w-full h-full object-contain"
                  data-testid="image-header-logo"
                />
              </div>
            )}
            <div className="flex flex-col justify-center">
              <h1
                className="font-poppins text-xl md:text-2xl font-bold text-foreground leading-tight"
                data-testid="text-header-title"
              >
                Sandalpur
              </h1>
              <p
                className="font-inter text-xs md:text-sm text-muted-foreground leading-tight"
                data-testid="text-header-subtitle"
              >
                Village Community
              </p>
            </div>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1.5" data-testid="nav-main">
            {/* Home + on-page sections */}
            <div className="relative group">
              <div className="pb-1">
                <button
                  className={`group/btn px-3 py-2 rounded-xl font-inter text-sm neo-button flex items-center gap-1 transition-all duration-200 ${
                    location === "/"
                      ? "bg-primary/10 text-primary shadow-md"
                      : "bg-background text-foreground hover:bg-primary/5"
                  }`}
                  data-testid="nav-link-home"
                  onClick={() => location !== "/" && setLocation("/")}
                >
                  <Home className={`w-4 h-4 ${location === "/" ? "text-primary" : "group-hover/btn:text-primary"}`} />
                  <span>Home</span>
                  <ChevronDown className={`w-3 h-3 ${location === "/" ? "text-primary" : "group-hover/btn:text-primary"}`} />
                </button>
              </div>
              <div className="absolute top-full left-0 w-48 pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="rounded-md border bg-popover p-1 text-popover-foreground shadow-md">
                  {navItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      data-testid={`nav-dropdown-link-${item.id}`}
                      className="w-full text-left px-2 py-1.5 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer flex items-center"
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Grouped page menus */}
            {NAV_GROUPS.map((group) => {
              const active = groupActive(group.links);
              const Icon = group.icon;
              return (
                <div key={group.id} className="relative group">
                  <div className="pb-1">
                    <button
                      className={`group/btn px-3 py-2 rounded-xl font-inter text-sm neo-button flex items-center gap-1 transition-all duration-200 ${
                        active
                          ? "bg-primary/10 text-primary shadow-md"
                          : "bg-background text-foreground hover:bg-primary/5"
                      }`}
                      data-testid={`nav-group-${group.id}`}
                    >
                      <Icon className={`w-4 h-4 ${active ? "text-primary" : "group-hover/btn:text-primary"}`} />
                      <span>{group.label}</span>
                      <ChevronDown className={`w-3 h-3 ${active ? "text-primary" : "group-hover/btn:text-primary"}`} />
                    </button>
                  </div>
                  <div className="absolute top-full left-0 w-56 pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="rounded-md border bg-popover p-1 text-popover-foreground shadow-md">
                      {group.links.map((link) => {
                        const LinkIconCmp = link.icon;
                        return (
                          <button
                            key={link.href}
                            onClick={() => setLocation(link.href)}
                            data-testid={`nav-link-${link.href.replace("/", "")}`}
                            className={`w-full text-left px-2 py-1.5 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer flex items-center gap-2 ${
                              location === link.href ? "text-primary font-medium" : ""
                            }`}
                          >
                            <LinkIconCmp className="w-4 h-4" />
                            {link.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </nav>

          <div className="flex items-center gap-1.5">
            <WeatherWidget />

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <button
                  className="md:hidden w-10 h-10 rounded-xl bg-background flex items-center justify-center neo-button"
                  data-testid="button-mobile-menu"
                >
                  <Menu className="w-5 h-5" />
                  <span className="sr-only">Open menu</span>
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="overflow-y-auto" data-testid="sheet-mobile-menu">
                <SheetHeader>
                  <SheetTitle className="font-poppins text-left">Navigation</SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col gap-2 mt-8" data-testid="nav-mobile">
                  {/* Home + on-page sections */}
                  <Collapsible open={homeMenuOpen} onOpenChange={setHomeMenuOpen}>
                    <CollapsibleTrigger asChild>
                      <button
                        className={`w-full px-4 py-3 rounded-xl font-inter text-base text-left neo-button flex items-center justify-between ${
                          location === "/" ? "bg-primary/10 text-primary" : "bg-background text-foreground"
                        }`}
                        data-testid="nav-mobile-link-home"
                      >
                        <div className="flex items-center gap-2">
                          <Home className={`w-4 h-4 ${location === "/" ? "text-primary" : ""}`} />
                          <span>Home</span>
                        </div>
                        <ChevronDown className={`w-4 h-4 transition-transform ${homeMenuOpen ? "rotate-180" : ""}`} />
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="pl-6 space-y-1 mt-1">
                        {navItems.map((item) => (
                          <button
                            key={item.id}
                            onClick={() => scrollToSection(item.id)}
                            className="w-full px-4 py-2 rounded-xl bg-background/50 text-foreground font-inter text-sm text-left neo-button"
                            data-testid={`nav-mobile-link-${item.id}`}
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  {/* Grouped pages — each collapsible */}
                  {NAV_GROUPS.map((group) => {
                    const Icon = group.icon;
                    const active = groupActive(group.links);
                    const isOpen = openGroup === group.id;
                    return (
                      <Collapsible
                        key={group.id}
                        open={isOpen}
                        onOpenChange={(o) => setOpenGroup(o ? group.id : null)}
                      >
                        <CollapsibleTrigger asChild>
                          <button
                            className={`w-full px-4 py-3 rounded-xl font-inter text-base text-left neo-button flex items-center justify-between ${
                              active ? "bg-primary/10 text-primary" : "bg-background text-foreground"
                            }`}
                            data-testid={`nav-mobile-group-${group.id}`}
                          >
                            <div className="flex items-center gap-2">
                              <Icon className={`w-4 h-4 ${active ? "text-primary" : ""}`} />
                              <span>{group.label}</span>
                            </div>
                            <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                          </button>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="pl-6 space-y-1 mt-1">
                            {group.links.map((link) => {
                              const LinkIconCmp = link.icon;
                              return (
                                <button
                                  key={link.href}
                                  onClick={() => go(link.href)}
                                  className={`w-full px-4 py-2 rounded-xl font-inter text-sm text-left neo-button flex items-center gap-2 ${
                                    location === link.href
                                      ? "bg-primary/10 text-primary"
                                      : "bg-background/50 text-foreground"
                                  }`}
                                  data-testid={`nav-mobile-link-${link.href.replace("/", "")}`}
                                >
                                  <LinkIconCmp className="w-4 h-4" />
                                  {link.label}
                                </button>
                              );
                            })}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    );
                  })}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
